import { useEffect, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimpleIcon, CopySimpleIcon, ArrowsClockwiseIcon, FileCodeIcon, BracketsCurlyIcon } from "@phosphor-icons/react";

const FORMATS = [
  { key: "cargrv2", label: "car.gr Vehicles XML", path: "/export/car-gr-vehicles.xml", type: "xml", filename: "car-gr-vehicles.xml", staticFeed: true },
  { key: "cargrpreview", label: "car.gr Preview (debug)", path: "/export/car-gr-vehicles-preview.xml", type: "xml", filename: "car-gr-preview.xml", staticFeed: true },
  { key: "cargrreport", label: "car.gr Validation Report", path: "/export/car-gr-validation-report.json", type: "json", filename: "car-gr-validation.json", staticFeed: true },
  { key: "wcsvcsv", label: "WooCommerce CSV (Polylang EL+EN)", path: "/export/woocommerce-products.csv", type: "csv", filename: "woocommerce-products.csv", staticFeed: true },
  { key: "wcxml", label: "WooCommerce XML (WP All Import)", path: "/export/woocommerce-products.xml", type: "xml", filename: "woocommerce-products.xml", staticFeed: true },
  { key: "wcreport", label: "WooCommerce Validation Report", path: "/export/woocommerce-validation-report.json", type: "json", filename: "woocommerce-validation.json", staticFeed: true },
  { key: "polycsv", label: "Polylang Vehicles CSV", path: "/export/polylang-vehicles.csv", type: "csv", filename: "polylang-vehicles.csv", staticFeed: true },
  { key: "polyxml", label: "Polylang Vehicles XML", path: "/export/polylang-vehicles.xml", type: "xml", filename: "polylang-vehicles.xml", staticFeed: true },
  { key: "polyreport", label: "Polylang Validation Report", path: "/export/polylang-validation-report.json", type: "json", filename: "polylang-validation.json", staticFeed: true },
  { key: "acfel", label: "ACF Fields (EL)", path: "/export/acf-fields-el.json", type: "json", filename: "acf-vehicle-fields-el.json", staticFeed: true },
  { key: "acfen", label: "ACF Fields (EN)", path: "/export/acf-fields-en.json", type: "json", filename: "acf-vehicle-fields-en.json", staticFeed: true },
  { key: "cptui", label: "CPT UI", path: "/export/cpt-ui.json", type: "json", filename: "cpt-ui-vehicle.json", staticFeed: true },
  { key: "cargr", label: "car.gr (legacy)", path: "/export/cargr.xml", type: "xml", filename: "cargr-feed.xml" },
  { key: "wordpress", label: "WordPress WXR (legacy)", path: "/export/wordpress.xml", type: "xml", filename: "wordpress-cars.xml" },
  { key: "google", label: "Google Merchant", path: "/export/google.xml", type: "xml", filename: "google-vehicles.xml" },
  { key: "json", label: "JSON", path: "/export/json", type: "json", filename: "cars.json" },
];

const MIME_TYPES = { csv: "text/csv", json: "application/json", xml: "application/xml" };

export default function Exports() {
  const [active, setActive] = useState("polycsv");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [stats, setStats] = useState(null);

  const current = FORMATS.find((f) => f.key === active);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    setContent("");
    try {
      const params = current.staticFeed ? {} : { only_available: onlyAvailable };
      if (current.type === "csv") {
        const r = await api.get(current.path, { params, responseType: "text", transformResponse: (d) => d });
        setContent(typeof r.data === "string" ? r.data : String(r.data));
      } else {
        const r = await api.get(current.path, { params });
        if (current.type === "json") {
          setContent(JSON.stringify(r.data, null, 2));
        } else {
          setContent(typeof r.data === "string" ? r.data : new XMLSerializer().serializeToString(r.data));
        }
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Σφάλμα";
      setContent(`<!-- Σφάλμα φόρτωσης feed: ${msg} -->`);
      toast.error(`Αποτυχία φόρτωσης feed: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [current, onlyAvailable]);

  useEffect(() => {
    fetchFeed();
    api.get("/cars/stats").then((r) => setStats(r.data));
  }, [fetchFeed]);

  const download = () => {
    const mime = MIME_TYPES[current.type] || "application/xml";
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = current.filename; a.click();
    URL.revokeObjectURL(url);
    toast.success("Λήψη ξεκίνησε");
  };

  const copyUrl = () => {
    const url = `${API}${current.path}${current.staticFeed ? "" : `?only_available=${onlyAvailable}`}`;
    navigator.clipboard.writeText(url);
    toast.success("Το URL αντιγράφηκε");
  };

  const copyContent = () => {
    navigator.clipboard.writeText(content);
    toast.success("Αντιγράφηκε");
  };

  return (
    <div className="space-y-6" data-testid="exports-page">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-t border-l border-black/10">
        <Cell label="Σύνολο" value={stats?.total ?? "·"} />
        <Cell label="Διαθέσιμα" value={stats?.available ?? "·"} accent="#16A34A" />
        <Cell label="Featured" value={stats?.featured ?? "·"} accent="#F59E0B" />
        <Cell label="Πωλημένα" value={stats?.sold ?? "·"} accent="#4B5563" />
      </div>

      {/* Tabs */}
      <div className="border border-black/10 bg-white">
        <div className="flex flex-wrap border-b border-black/10">
          {FORMATS.map((f) => (
            <button
              key={f.key}
              onClick={() => setActive(f.key)}
              data-testid={`tab-${f.key}`}
              className={`px-5 py-3 text-sm font-medium border-r border-black/10 transition-colors flex items-center gap-2 ${
                active === f.key ? "bg-[#0A0A0A] text-white" : "hover:bg-[#F9FAFB]"
              }`}
            >
              {f.type === "json" ? <BracketsCurlyIcon size={16} /> : <FileCodeIcon size={16} />}
              {f.label}
            </button>
          ))}
        </div>

        <div className="px-5 py-3 border-b border-black/10 bg-[#F9FAFB] flex flex-wrap items-center justify-between gap-3">
          <label className={`flex items-center gap-2 text-sm cursor-pointer ${current.staticFeed ? "opacity-40 cursor-not-allowed" : ""}`}>
            <input
              type="checkbox"
              checked={onlyAvailable}
              onChange={(e) => setOnlyAvailable(e.target.checked)}
              disabled={current.staticFeed}
              data-testid="only-available-toggle"
            />
            Μόνο διαθέσιμα οχήματα
            {current.staticFeed && <span className="text-[10px] uppercase tracking-wider text-[#4B5563]">(δεν εφαρμόζεται)</span>}
          </label>
          <div className="flex gap-2">
            <button onClick={fetchFeed} className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5" data-testid="refresh-btn">
              <ArrowsClockwiseIcon size={14} /> Ανανέωση
            </button>
            <button onClick={copyUrl} className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5" data-testid="copy-url-btn">
              <CopySimpleIcon size={14} /> URL Feed
            </button>
            <button onClick={copyContent} className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5" data-testid="copy-content-btn">
              <CopySimpleIcon size={14} /> Περιεχόμενο
            </button>
            <button onClick={download} className="btn-primary text-xs py-2 px-3 flex items-center gap-1.5" data-testid="download-btn">
              <DownloadSimpleIcon size={14} /> Λήψη
            </button>
          </div>
        </div>

        {/* Preview */}
        <div className="code-panel p-5 max-h-[70vh] overflow-auto" data-testid="export-preview">
          {loading ? (
            <div className="text-white/60">Φόρτωση...</div>
          ) : (
            <pre className="whitespace-pre-wrap text-xs">{content}</pre>
          )}
        </div>
      </div>

      {/* Help */}
      <div className="border border-black/10 p-6 bg-[#F9FAFB]">
        <h3 className="heading text-base font-bold mb-2">Οδηγίες χρήσης</h3>
        <ul className="text-sm text-[#4B5563] space-y-1.5 list-disc list-inside">
          <li><strong>car.gr</strong>: Δώστε στο car.gr το URL του feed για αυτόματη ενημέρωση αγγελιών.</li>
          <li><strong>WordPress (WXR)</strong>: WP Admin → Εργαλεία → Εισαγωγή → WordPress. Εισαγάγει τα αυτοκίνητα ως post type <code className="bg-white px-1">vehicle</code> με όλα τα πεδία ως postmeta.</li>
          <li><strong>CPT UI</strong>: WP Admin → CPT UI → Εργαλεία → Tab «Import/Export Post Types» → επικολλήστε το περιεχόμενο JSON και πατήστε Import. Δημιουργεί το post type <code className="bg-white px-1">vehicle</code> με Ελληνικά labels.</li>
          <li><strong>ACF Fields</strong>: WP Admin → ACF → Ομάδες Πεδίων → «Εισαγωγή JSON» → ανεβάστε το αρχείο. Δημιουργεί 28 πεδία συνδεδεμένα με το post type <code className="bg-white px-1">vehicle</code>.</li>
          <li><strong>Google Merchant</strong>: Φορτώστε το XML στο Google Merchant Center για Vehicle Ads.</li>
          <li><strong>JSON</strong>: Γενικής χρήσης για custom integrations.</li>
        </ul>
        <div className="mt-3 p-3 bg-white border border-black/10 text-xs font-mono break-all">
          {`${API}${current.path}${current.staticFeed ? "" : `?only_available=${onlyAvailable}`}`}
        </div>
        <div className="mt-4 pt-3 border-t border-black/10">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563] mb-2">Συνιστώμενη σειρά εγκατάστασης WordPress</div>
          <ol className="text-sm text-[#4B5563] space-y-1 list-decimal list-inside">
            <li>Εγκαταστήστε τα plugins: <em>Custom Post Type UI</em> + <em>Advanced Custom Fields (ACF)</em></li>
            <li>Κάντε import το <strong>CPT UI</strong> JSON (δημιουργείται το post type «vehicle»)</li>
            <li>Κάντε import το <strong>ACF Fields</strong> JSON (συνδέονται όλα τα πεδία)</li>
            <li>Κάντε import το <strong>WordPress WXR</strong> XML (φορτώνονται τα αυτοκίνητα με τα δεδομένα)</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, accent }) {
  return (
    <div className="border-b border-r border-black/10 p-5">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">{label}</div>
      <div className="heading text-3xl font-extrabold mt-1" style={{ color: accent || "#0A0A0A" }}>{value}</div>
    </div>
  );
}
